import {LayoutStore} from 'src/app/services/ui/layout.store';
import {computed, inject} from '@angular/core';
import {patchState, signalStore, withComputed, withMethods, withState} from '@ngrx/signals';
import {rxMethod} from '@ngrx/signals/rxjs-interop';
import {catchError, debounceTime, distinctUntilChanged, EMPTY, pipe, switchMap} from 'rxjs';
import {map, tap} from 'rxjs/operators';
import {GlobalResearchService} from 'src/app/services/search/global-research.service';
import {GlobalResearch, GlobalResearchSection, SearchEntityType, SearchItem} from 'src/app/services/search/global-research.model';
import {CurrentTenantStore} from 'src/app/services/tenant/current-tenant.store';
import {Permission} from 'src/app/services/auth/permissions.service';
import {LoadError, toLoadError} from 'src/app/services/shared/load-error.model';

interface GlobalResearchState {
	query: string;
	result: GlobalResearch | undefined;
	loading: boolean;
	/** Why the search failed: shown where its results were expected (KAB-2441). */
	error: LoadError | null;
	/** Type filters toggled by the user — empty means "All". */
	selectedTypes: SearchEntityType[];
	/** Last distinct resources the user viewed (from audit logs), shown while no search is active. */
	suggestions: SearchItem[];
	suggestionsLoading: boolean;
	suggestionsError: LoadError | null;
}

const initialState: GlobalResearchState = {
	query: '', result: undefined, loading: false, error: null, selectedTypes: [], suggestions: [], suggestionsLoading: false, suggestionsError: null,
};

// Mirrors the backend's MIN_QUERY_LENGTH (GlobalSearchController rejects shorter queries with a 400).
const MIN_QUERY_LENGTH = 2;

// Mirrors the backend's MAX_QUERY_LENGTH (and the input's maxlength); anything longer would be a 400.
const MAX_QUERY_LENGTH = 100;

// Filter chips shown inline; the rest go into the "see more" menu.
const VISIBLE_FILTER_COUNT = 3;

const SECTION_ORDER: SearchEntityType[] = [
	SearchEntityType.APPLICATION,
	SearchEntityType.SERVER,
	SearchEntityType.USER,
	SearchEntityType.DATA,
	SearchEntityType.NETWORK,
	SearchEntityType.ROUTER,
	SearchEntityType.WORKSTATION,
	SearchEntityType.MOBILE_DEVICE,
	SearchEntityType.ORGANIZATION
];

// Mirrors the backend's gating: servers behind Servers, networks/routers behind Networks, workstations
// and mobile devices behind Devices (GlobalSearchQueryService).
const PERMISSION_BY_TYPE: Record<SearchEntityType, Permission> = {
	[SearchEntityType.APPLICATION]: Permission.APPLICATIONS_READ,
	[SearchEntityType.SERVER]: Permission.SERVERS_READ,
	[SearchEntityType.USER]: Permission.USERS_READ,
	[SearchEntityType.NETWORK]: Permission.NETWORKS_READ,
	[SearchEntityType.ROUTER]: Permission.NETWORKS_READ,
	[SearchEntityType.DATA]: Permission.DATA_READ,
	[SearchEntityType.WORKSTATION]: Permission.DEVICES_READ,
	[SearchEntityType.MOBILE_DEVICE]: Permission.DEVICES_READ,
	[SearchEntityType.ORGANIZATION]: Permission.ORGANISATION_READ
};

// No `providedIn` → provided by GlobalResearchComponent so the search state lives and dies with the dialog.
export const GlobalResearchStore = signalStore(
	withState<GlobalResearchState>(initialState),
	withComputed((store) => {
		const layout = inject(LayoutStore);
		const tenant = inject(CurrentTenantStore);
		// Types the user has the read permission for — mirrors the main menu's own gating,
		// so a filter chip never offers a section the user couldn't otherwise reach.
		const permittedTypes = computed<SearchEntityType[]>(() =>
			SECTION_ORDER.filter(type => tenant.hasAnyPermission([PERMISSION_BY_TYPE[type]])));
		// Before any search every permitted type is proposed; once results are in, only the types actually present.
		const availableTypes = computed<SearchEntityType[]>(() => {
			const result = store.result();
			const permitted = permittedTypes();
			if (!result) {
				return permitted;
			}
			return permitted.filter(type => result.items.some(item => item.type === type));
		});
		// Chips keep their natural order, except a type selected from the "see more" menu:
		// it is hoisted to the front so it becomes an inline chip while selected.
		const orderedFilterTypes = computed<SearchEntityType[]>(() => {
			const available = availableTypes();
			const hoisted = store.selectedTypes()
				.filter(type => available.indexOf(type) >= VISIBLE_FILTER_COUNT);
			if (hoisted.length === 0) {
				return available;
			}
			return [...hoisted, ...available.filter(type => !hoisted.includes(type))];
		});
		// A "see more" menu holding a single item is pointless: when only one type would
		// overflow, show it inline instead and the menu disappears.
		// Téléphone : toutes les puces en ligne (rangée défilante), pas de menu « voir plus ».
		const filterCutoff = computed(() => {
			const total = orderedFilterTypes().length;
			if (layout.isHandset()) return total;
			return total <= VISIBLE_FILTER_COUNT + 1 ? total : VISIBLE_FILTER_COUNT;
		});
		return {
			availableTypes,
			visibleFilterTypes: computed(() => orderedFilterTypes().slice(0, filterCutoff())),
			overflowFilterTypes: computed(() => orderedFilterTypes().slice(filterCutoff())),
			isAllSelected: computed(() => store.selectedTypes().length === 0),
			// Suggestions honor the type filters too, so a selected chip never shows off-type items.
			visibleSuggestions: computed<SearchItem[]>(() => {
				const selected = store.selectedTypes();
				const suggestions = store.suggestions();
				return selected.length > 0 ? suggestions.filter(item => selected.includes(item.type)) : suggestions;
			}),
			sections: computed<GlobalResearchSection[]>(() => {
				const result = store.result();
				if (!result) {
					return [];
				}
				const selected = store.selectedTypes();
				const visibleTypes = selected.length > 0 ? selected : availableTypes();
				return SECTION_ORDER
					.filter(type => visibleTypes.includes(type))
					.map(type => ({type, items: result.items.filter(item => item.type === type)}))
					.filter(section => section.items.length > 0);
			}),
			total: computed(() => store.result()?.total ?? 0),
		};
	}),
	withMethods((store) => {
		const api = inject(GlobalResearchService);
		const tenant = inject(CurrentTenantStore);
		// Fetches the results of a query; retrySearch() runs it again past search()'s distinctUntilChanged.
		const fetchResults = rxMethod<string>(pipe(
			switchMap(query => {
				const tenantId = tenant.currentTenantId();
				if (!tenantId || query.length < MIN_QUERY_LENGTH) {
					patchState(store, {result: undefined, loading: false, error: null, selectedTypes: []});
					return EMPTY;
				}
				patchState(store, {loading: true, error: null});
				return api.search(tenantId, query).pipe(
					tap(result => {
						// Drop selected filters whose type vanished from the new results, so no invisible filter sticks.
						const available = new Set(result.items.map(item => item.type));
						patchState(store, {
							result,
							loading: false,
							selectedTypes: store.selectedTypes().filter(type => available.has(type)),
						});
					}),
					// Caught inside the switchMap: an error reaching the rxMethod would end the search.
					catchError(error => {
						patchState(store, {result: undefined, loading: false, error: toLoadError(error)});
						return EMPTY;
					}),
				);
			}),
		));
		const search = rxMethod<string>(pipe(
			// The input's maxlength blocks typing past the limit; the slice covers programmatic values.
			map(query => query.trim().slice(0, MAX_QUERY_LENGTH)),
			tap(query => patchState(store, {query})),
			debounceTime(300),
			distinctUntilChanged(),
			tap(query => fetchResults(query)),
		));
		const loadSuggestions = rxMethod<void>(pipe(
			tap(() => patchState(store, {suggestionsLoading: true, suggestionsError: null})),
			switchMap(() => {
				const tenantId = tenant.currentTenantId();
				if (!tenantId) {
					patchState(store, {suggestionsLoading: false});
					return EMPTY;
				}
				return api.suggestions(tenantId).pipe(
					tap(suggestions => patchState(store, {suggestions, suggestionsLoading: false})),
					catchError(error => {
						patchState(store, {suggestionsLoading: false, suggestionsError: toLoadError(error)});
						return EMPTY;
					}),
				);
			}),
		));
		return {
			search,
			retrySearch(): void {
				fetchResults(store.query());
			},
			loadSuggestions,
			// Single-select (the API accepts a list, but the UI allows one type at a time):
			// picking a chip replaces the previous selection; picking it again clears back to "All".
			toggleType(type: SearchEntityType): void {
				patchState(store, {selectedTypes: store.selectedTypes().includes(type) ? [] : [type]});
			},
			selectAllTypes(): void {
				patchState(store, {selectedTypes: []});
			},
		};
	}),
);
